import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { PlusButton } from "@/components/design/PlusButton";

// Freie Schlagworte am Projekt, zum Clustern. Bewusst ohne Katalog und ohne
// Vorschlagsliste: was ein Projekt ausmacht, weiß beim Anlegen nur der Mensch
// davor. Die KI-Anreicherung kommt später und schreibt in dasselbe Feld.
//
// Chips im Stil von `.smycles-chip-button`, nur nicht als Knopf, weil ein Tag
// nichts auslöst; das × daneben ist der Knopf. Neue Tags kommen über ein Feld,
// das erst auf Anforderung erscheint - eine leere Eingabezeile in einer sonst
// leeren Box wäre die lauteste Stelle des Screens.
//
// Das Eingabefeld trägt dieselbe Pillenform wie die Chips daneben, statt als
// Kasten unter ihnen zu stehen: man schreibt sichtbar in die Form, die gleich
// entsteht, und die Zeile springt beim Abschicken nicht um.

export function ProjectTags({
  tags,
  onChange,
  readOnly = false,
}: {
  tags: readonly string[];
  onChange: (next: string[]) => void;
  readOnly?: boolean;
}) {
  const intl = useIntl();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  function add() {
    const value = draft.trim();
    // Doppelte Tags sind kein Fehler, den man dem Nutzer melden muss - sie
    // werden einfach nicht zweimal aufgenommen.
    if (value && !tags.includes(value)) onChange([...tags, value]);
    setDraft("");
    setAdding(false);
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Ein Tag ist nur sein Text, und der taugt nicht als id; gezählt wird deshalb
          innerhalb der Liste, der Inhalt wird am Anker geprüft. */}
      {tags.map((tag, i) => (
        <span
          key={tag}
          id={`project-tags-item-${i}`}
          className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-smycles-label text-ink ring-1 ring-ink/15"
        >
          {tag}
          {readOnly ? null : (
            <button
              id={`project-tags-remove-${i}`}
              type="button"
              className="text-ink-muted transition hover:text-ink"
              aria-label={intl.formatMessage(
                { id: "project.tags.remove", defaultMessage: "{tag} entfernen" },
                { tag },
              )}
              onClick={() => onChange(tags.filter((t) => t !== tag))}
            >
              ×
            </button>
          )}
        </span>
      ))}

      {readOnly ? (
        tags.length === 0 ? <span className="smycles-field-meta text-ink-muted">–</span> : null
      ) : adding ? (
        <span className="inline-flex items-center rounded-full px-3 py-1.5 text-smycles-label ring-1 ring-ink-ui">
          <input
            id="project-tags-input"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={add}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add();
              }
              if (e.key === "Escape") {
                setDraft("");
                setAdding(false);
              }
            }}
            aria-label={intl.formatMessage({ id: "project.tags.new", defaultMessage: "Neues Tag" })}
            placeholder={intl.formatMessage({
              id: "project.tags.placeholder",
              defaultMessage: "Tag und Enter",
            })}
            // Wächst mit dem Text, startet aber breit genug für ein kurzes Wort.
            className="min-w-[9ch] max-w-[24ch] bg-transparent text-smycles-label text-ink outline-none placeholder:text-ink-muted [field-sizing:content]"
          />
        </span>
      ) : (
        <PlusButton
          id="project-tags-add"
          label={intl.formatMessage({ id: "project.tags.add", defaultMessage: "Tag" })}
          onClick={() => setAdding(true)}
        />
      )}

      {tags.length === 0 && !adding && !readOnly ? (
        <span className="smycles-field-meta text-ink-muted">
          <FormattedMessage
            id="project.tags.empty"
            defaultMessage="Noch keine Tags. Sie helfen später beim Finden ähnlicher Projekte."
          />
        </span>
      ) : null}
    </div>
  );
}
